import React, { createContext, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { io } from "socket.io-client";
import { getApiBaseUrl } from "../utils/baseUrl";
import { getUserFromToken } from "../utils/auth";

export const NotificationContext = createContext();

const notificationId = (item) => String(item?._id || item?.id || "");

const NotificationProvider = ({ children, currentRole, currentSubBranch }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);
  const [browserPermission, setBrowserPermission] = useState(
    "Notification" in window ? Notification.permission : "unsupported"
  );
  const [soundEnabled, setSoundEnabled] = useState(
    localStorage.getItem("waveyNotificationSound") === "true"
  );
  const recentNotificationKeysRef = useRef(new Map());
  const knownNotificationIdsRef = useRef(new Set());

  const token = localStorage.getItem("authToken") || sessionStorage.getItem("authToken");
  const currentUser = useMemo(() => getUserFromToken(token), [token]);
  const currentUserId = String(currentUser?.userId || currentUser?._id || "");

  const isRead = useCallback(
    (item) => Array.isArray(item?.readBy) && item.readBy.map(String).includes(currentUserId),
    [currentUserId]
  );

  const showDesktopNotification = useCallback((item) => {
    if (browserPermission !== "granted") return;
    new Notification(item.title || "Document update", {
      body: item.message || "A document action occurred.",
      icon: "/notification-icon.png",
      tag: notificationId(item) || undefined,
    });
  }, [browserPermission]);

  const playNotificationSound = useCallback(() => {
    if (!soundEnabled) return;
    new Audio("/notification.mp3").play().catch(() => {});
  }, [soundEnabled]);

  const addNotification = useCallback((item) => {
    const roleMismatch = item.role && String(item.role).toUpperCase() !== String(currentRole || "").toUpperCase();
    const branchMismatch = item.subBranch && String(item.subBranch).toUpperCase() !== String(currentSubBranch || "").toUpperCase();
    if (roleMismatch || branchMismatch) return;

    const duplicateKey = `${item.eventType || "LOCAL"}|${item.title || ""}|${item.message || ""}`;
    const previousTime = recentNotificationKeysRef.current.get(duplicateKey) || 0;
    if (Date.now() - previousTime < 8000) return;
    recentNotificationKeysRef.current.set(duplicateKey, Date.now());

    const localItem = {
      ...item,
      id: notificationId(item) || `local-${Date.now()}`,
      title: item.title || "Document update",
      message: item.message || "A document action occurred.",
      createdAt: item.createdAt || new Date().toISOString(),
      readBy: item.readBy || [],
    };
    knownNotificationIdsRef.current.add(localItem.id);
    setNotifications((previous) => [localItem, ...previous.filter((entry) => notificationId(entry) !== localItem.id)]);
    setUnreadCount((count) => count + 1);
    showDesktopNotification(localItem);
    playNotificationSound();
  }, [currentRole, currentSubBranch, playNotificationSound, showDesktopNotification]);

  const markRead = useCallback(async (id) => {
    const target = notifications.find((item) => notificationId(item) === String(id));
    if (!target || isRead(target)) return;

    setNotifications((previous) => previous.map((item) =>
      notificationId(item) === String(id)
        ? { ...item, readBy: Array.from(new Set([...(item.readBy || []), currentUserId])) }
        : item
    ));
    setUnreadCount((count) => Math.max(0, count - 1));

    if (!token || String(id).startsWith("local-")) return;
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/notifications/${id}/read`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error("Unable to mark notification as read.");
    } catch {
      setUnreadCount((count) => count + 1);
    }
  }, [currentUserId, isRead, notifications, token]);

  const markAllRead = useCallback(async () => {
    const previousUnread = unreadCount;
    setNotifications((previous) => previous.map((item) => ({
      ...item,
      readBy: Array.from(new Set([...(item.readBy || []), currentUserId])),
    })));
    setUnreadCount(0);
    if (!token) return;
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/notifications/read-all`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error("Unable to mark all notifications as read.");
    } catch {
      setUnreadCount(previousUnread);
    }
  }, [currentUserId, token, unreadCount]);

  const dismissNotification = useCallback((id) => {
    setNotifications((previous) => previous.filter((item) => notificationId(item) !== String(id)));
  }, []);

  const enableBrowserNotifications = useCallback(async () => {
    if (!("Notification" in window)) return "unsupported";
    const permission = await Notification.requestPermission();
    setBrowserPermission(permission);
    return permission;
  }, []);

  const toggleSound = useCallback(() => {
    setSoundEnabled((enabled) => {
      const next = !enabled;
      localStorage.setItem("waveyNotificationSound", String(next));
      return next;
    });
  }, []);

  useEffect(() => {
    if (!token) return undefined;
    const baseUrl = getApiBaseUrl();
    let active = true;

    fetch(`${baseUrl}/api/notifications?limit=50`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((response) => response.ok ? response.json() : Promise.reject(new Error("Notification fetch failed")))
      .then((data) => {
        if (!active) return;
        const savedNotifications = (data.notifications || []).map((item) => ({ ...item, id: notificationId(item) }));
        knownNotificationIdsRef.current = new Set(savedNotifications.map((item) => item.id));
        setNotifications(savedNotifications);
        setUnreadCount(Number(data.unread || 0));
      })
      .catch(() => {});

    const notificationSocket = io(baseUrl, {
      path: "/socket.io",
      auth: { token },
      transports: ["polling", "websocket"],
    });
    notificationSocket.on("documentNotification", (item) => {
      if (!active) return;
      const incomingId = notificationId(item);
      if (incomingId && knownNotificationIdsRef.current.has(incomingId)) return;
      const duplicateKey = `${item.eventType || "DOCUMENT_UPDATE"}|${item.title || ""}|${item.message || ""}`;
      const previousTime = recentNotificationKeysRef.current.get(duplicateKey) || 0;
      if (Date.now() - previousTime < 8000) return;
      recentNotificationKeysRef.current.set(duplicateKey, Date.now());
      const incoming = { ...item, id: incomingId, readBy: item.readBy || [] };
      if (incomingId) knownNotificationIdsRef.current.add(incomingId);
      setNotifications((previous) => [incoming, ...previous.filter((entry) => notificationId(entry) !== incoming.id)]);
      setUnreadCount((count) => count + 1);
      showDesktopNotification(incoming);
      playNotificationSound();
    });

    return () => {
      active = false;
      notificationSocket.disconnect();
    };
  }, [currentRole, currentSubBranch, playNotificationSound, showDesktopNotification, token]);

  return (
    <NotificationContext.Provider value={{
      notifications,
      unreadCount,
      isRead,
      addNotification,
      markRead,
      markAllRead,
      dismissNotification,
      removeNotification: dismissNotification,
      browserPermission,
      enableBrowserNotifications,
      soundEnabled,
      toggleSound,
      pendingCount,
      setPendingCount,
      incrementPending: () => setPendingCount((count) => count + 1),
      decrementPending: () => setPendingCount((count) => Math.max(0, count - 1)),
      resetPending: (count = 0) => setPendingCount(count),
    }}>
      {children}
    </NotificationContext.Provider>
  );
};

export default NotificationProvider;
